'use client';

import { useState } from 'react';
import { useTranslations } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';
import Link from '../link';
import BevSafeText from '../bev-safe-text';
import { BreadcrumbProps } from './types';

// "/" divider drawn after a crumb, matching the legacy `li + li::after` / `.ellipsis::after` rules.
// A hidden crumb renders no pseudo-element, so the first visible crumb never gets a leading slash.
const SEPARATOR = "after:content-['/'] after:mx-inline-xs after:text-text-secondary";

// Trailing crumbs kept visible while collapsed, per viewport (legacy `show-last` values: base 2 / md 4 / lg 5 / xl 6).
const VISIBLE_WHILE_COLLAPSED = { base: 2, md: 4, lg: 5, xl: 6 } as const;

export default function Breadcrumb({ items, className, fullWidth }: BreadcrumbProps) {
	const t = useTranslations();
	const [expanded, setExpanded] = useState(false);

	if (items.length === 0) {
		return null;
	}

	const count = items.length;
	// Ellipsis is only meaningful when more crumbs exist than the viewport keeps visible.
	const needsEllipsis = {
		base: count > VISIBLE_WHILE_COLLAPSED.base,
		md: count > VISIBLE_WHILE_COLLAPSED.md,
		lg: count > VISIBLE_WHILE_COLLAPSED.lg,
		xl: count > VISIBLE_WHILE_COLLAPSED.xl,
	};

	return (
		// A card-padding-s top inset — the hbs `<ol>`'s
		// `padding-top: var(--…-card-padding-s)`, always. No bottom spacing: the
		// parent owns the rhythm via a `spacing-stack-*` flex column (see the PDP
		// heroes / the preferred-partner pages).
		<nav aria-label={t('Breadcrumb')} className={cn('pt-spacing-card-padding-s', className)}>
			{/* `spacing-wide-grid` == fs-blacklight's `wide-grid-container` (hbs put it
			    on the <ol>). `fullWidth` drops it for parents that constrain the trail
			    themselves. */}
			<ol
				className={cn(
					'flex flex-row flex-wrap items-center list-none',
					fullWidth ? 'p-0 m-0' : 'spacing-wide-grid'
				)}
			>
				<li
					className={cn(
						'items-center whitespace-nowrap text-caption text-text-secondary',
						SEPARATOR,
						expanded || !needsEllipsis.base ? 'hidden' : 'flex',
						!expanded && (needsEllipsis.md ? 'md:flex' : 'md:hidden'),
						!expanded && (needsEllipsis.lg ? 'lg:flex' : 'lg:hidden'),
						!expanded && (needsEllipsis.xl ? 'xl:flex' : 'xl:hidden')
					)}
				>
					<button
						type="button"
						aria-label={t('Expand breadcrumb')}
						aria-expanded={expanded}
						onClick={() => setExpanded(true)}
						className="cursor-pointer whitespace-nowrap"
					>
						...
					</button>
				</li>
				{items.map(({ label, href, onClick }, index) => {
					const isLast = index === count - 1;
					const fromEnd = count - 1 - index;

					return (
						<li
							key={`${label}-${index}`}
							aria-current={isLast ? 'page' : undefined}
							className={cn(
								'items-center whitespace-nowrap text-caption',
								!isLast && SEPARATOR,
								expanded ? 'flex' : fromEnd < VISIBLE_WHILE_COLLAPSED.base ? 'flex' : 'hidden',
								!expanded && (fromEnd < VISIBLE_WHILE_COLLAPSED.md ? 'md:flex' : 'md:hidden'),
								!expanded && (fromEnd < VISIBLE_WHILE_COLLAPSED.lg ? 'lg:flex' : 'lg:hidden'),
								!expanded && (fromEnd < VISIBLE_WHILE_COLLAPSED.xl ? 'xl:flex' : 'xl:hidden')
							)}
						>
							{!isLast && href ? (
								<Link
									href={href}
									onClick={onClick}
									intent="unstyled"
									prefetch={false}
									className="text-caption text-text-secondary whitespace-nowrap hover:underline underline-offset-[5px]"
								>
									<BevSafeText>{label}</BevSafeText>
								</Link>
							) : (
								<span className={isLast ? 'text-text-heading' : 'text-text-secondary'}>
									<BevSafeText>{label}</BevSafeText>
								</span>
							)}
						</li>
					);
				})}
			</ol>
		</nav>
	);
}
